/** Pure helpers for the Text Requests tab: local-time labels and program status copy. */

import type { ProgramView, SmsProgramStatus, TextStatus } from '../api/reviewSmsApi';
import { timeZoneName } from '@/shared/utils/timezones';

export { timeZoneName, PRACTICE_TIME_ZONES } from '@/shared/utils/timezones';

/**
 * A time of day in the practice's zone. `withZone` appends the short zone name ("9:30 AM PT") so a
 * wrong practice time zone is visible instead of silently shifting every time.
 */
export function formatLocalTime(iso: string, timezone: string, withZone = false): string {
  try {
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso));
    return withZone ? `${time} ${timeZoneName(timezone, 'short')}` : time;
  } catch {
    // An unknown zone: show UTC and say so, never the viewer's own zone dressed up as the practice's.
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso));
    return `${time} UTC`;
  }
}

/**
 * A message time with its day, in the practice's zone: "5:39 PM PT" today, "Yesterday 6:36 PM PT",
 * "Mon, Oct 5 · 5:39 PM PT" before that. Threads span days (a reminder lands a day later), so a
 * bare time reads as if everything happened this evening.
 */
export function formatMessageTime(iso: string, timezone: string, now: Date = new Date()): string {
  const time = formatLocalTime(iso, timezone, true);
  const day = dayKeyIn(new Date(iso), timezone);
  if (day === dayKeyIn(now, timezone)) return time;
  if (day === dayKeyIn(new Date(now.getTime() - 86_400_000), timezone)) return `Yesterday ${time}`;
  try {
    const date = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
    return `${date} · ${time}`;
  } catch {
    return `${new Date(iso).toISOString().slice(0, 10)} · ${time}`;
  }
}

function dayKeyIn(d: Date, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function formatDayLabel(dateKey: string, timezone: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const noon = new Date(Date.UTC(y, m - 1, d, 12));
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(noon);
  } catch {
    return dateKey;
  }
}

/**
 * Backend `VISIT_MAX_AGE_DAYS`: visits can be uploaded and confirmed up to this many days after
 * they happen (advance uploads are always fine); older ones are not accepted.
 */
export const VISIT_MAX_AGE_DAYS = 30;
/** How far ahead the Today tab pages, for schedules uploaded in advance. */
export const ADVANCE_DAYS_VIEWABLE = 60;

export function shiftDateKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days, 12));
  return t.toISOString().slice(0, 10);
}

export function todayKey(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    return parts;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export const STATUS_LABEL: Record<SmsProgramStatus, string> = {
  not_started: 'Not set up',
  setup: 'Setup',
  awaiting_registration: 'Awaiting registration',
  live: 'Live · sending',
  paused: 'Paused',
};

export function statusTone(status: SmsProgramStatus): 'live' | 'setup' | 'paused' {
  if (status === 'live') return 'live';
  if (status === 'paused') return 'paused';
  return 'setup';
}

/** The skip reasons the roster and the kernel record, in words the front desk uses. */
export const SKIP_REASON_LABEL: Record<string, string> = {
  suppressed: 'Opted out',
  landline: "Landline, can't text",
  invalid_number: 'Number not valid',
  no_consent: 'Not OK to text',
  recently_contacted: 'Texted in the last 60 days',
  open_conversation: 'Not sent · already in a conversation with Betty',
  stale: 'Too late to text',
  too_old: 'More than 30 days ago, too late to text',
  staff_excluded: 'Staff chose not to text',
  flag_off: 'Texting is switched off',
  superseded_test: 'Replaced by a newer conversation',
  not_in_latest_upload: 'Not in the latest schedule upload',
  frequency_cap: 'Not sent · texted in the last 60 days',
  program_not_live: "Not sent · texting wasn't live",
  test_mode: 'Not sent · test mode was on (tests only)',
  carrier_blocked: 'Not sent · the carrier blocked it',
  unroutable: "Not sent · the number can't get texts",
  send_failed: 'Not sent · the text could not go out',
};

export function textStatusLabel(text: TextStatus): string {
  switch (text.kind) {
    case 'sent':
      return 'Text created · see Conversations';
    case 'queued':
      return 'Handed to Betty';
    case 'waiting_confirmation':
      return 'Waiting for confirmation';
    case 'scheduled':
      return 'After the visit';
    case 'skipped':
      return SKIP_REASON_LABEL[text.reason] ?? text.reason;
    case 'no_show':
      return 'No text (no-show)';
    case 'cancelled':
      return 'No text (cancelled)';
    case 'excluded':
      return 'Staff chose not to text';
  }
}

/**
 * Live for patients. Test mode also sets the status to 'live' (so the test can text), but patients
 * are texted only after Go live, which turns test mode off.
 */
export function isLive(program: Pick<ProgramView, 'status' | 'test'>): boolean {
  return program.status === 'live' && !program.test.enabled;
}

/** Which wizard steps count as done, from the program alone (the wizard has no state of its own). */
export function stepsDone(program: ProgramView): boolean[] {
  const live = isLive(program);
  const timingDone = program.status !== 'not_started';
  // The default texts are complete, compliant messages: keeping them is a valid choice, and saving
  // them unchanged writes nothing (no version), so the step counts as done once setup has begun.
  const messagesDone = timingDone || program.messages.firstTextVersion > 0 || live;
  // Nothing to fill in any more (the reminder is an email to the team): done once reached.
  const routineDone = messagesDone;
  const liveDone = live && textingReadiness(program).ready;
  return [timingDone, messagesDone, routineDone, liveDone];
}

/**
 * Texting number and carrier registration (set up in Settings → Communication → Texting). Nothing texts, tests
 * included, until both are done; the backend enforces the same rule.
 */
export function textingReadiness(program: ProgramView): { numberLive: boolean; ready: boolean } {
  const numberLive = program.sendingNumber.provisioningState === 'active';
  return { numberLive, ready: numberLive && program.registration.approved };
}

export function hourLabel(h: number): string {
  const hh = ((h + 11) % 12) + 1;
  return `${hh} ${h < 12 ? 'AM' : 'PM'}`;
}
